import {
  ITextObjectStyleConfiguration,
  IBackgroundColorConfiguration,
  IBorderColorConfiguration,
  IObjectColorConfiguration,
  IEnableConfiguration,
} from "../../../../domain/configuration";
import { TextBoxInputMethodOptionKinds } from "../../../../domain/document/inputMethodOptionKinds";
import { TextBoxInputMethodKinds } from "../../../../domain/document/inputMethodKinds/TextBoxInputMethodKinds";
import {
  PaintStyle,
  TextStyle,
} from "../../../../domain/drawing";
import {
  TextBoxItem,
} from "../../../../domain/document/items";
import { AbstractTextObjectStyleReader } from "./AbstractTextObjectStyleReader";
import { FixedColorSource } from "./FixedColorSource";

export class TextObjectStyleReader extends AbstractTextObjectStyleReader<TextBoxItem> {
  constructor(private readonly _configuration: ITextObjectStyleConfiguration) {
    super();
  }

  protected readSpecifiedPaintStyle(source: TextBoxItem, shouldInputFree: boolean): PaintStyle {
    return source.paintStyleOnEditing;
  }

  protected readBackgroundColorConfiguration(source: TextBoxItem, shouldInputFree: boolean): IBackgroundColorConfiguration {
    return this.readObjectStyleConfiguration(source, shouldInputFree);
  }

  protected readBorderColorConfiguration(source: TextBoxItem, shouldInputFree: boolean): IBorderColorConfiguration {
    return this.readObjectStyleConfiguration(source, shouldInputFree);
  }

  private readObjectStyleConfiguration(source: TextBoxItem, shouldInputFree: boolean): IObjectColorConfiguration {
    if (source.injectedRule && source.injectedRule.isEditDisabled) {
      return FixedColorSource.uneditable;
    }
    if (!source.shouldInputFree && !(source.isFreeInputable && shouldInputFree)) {
      switch (source.inputMethodOptionKind) {
        case TextBoxInputMethodOptionKinds.HisFreeInput:
        case TextBoxInputMethodOptionKinds.HisInfoSelect:
        case TextBoxInputMethodOptionKinds.HisItem:
          return this._configuration.hisInformation;
        case TextBoxInputMethodOptionKinds.UserDefinition:
          return this._configuration.userDefinition;
        case TextBoxInputMethodOptionKinds.MKSTemplate:
          return this._configuration.mksTemplate;
        case TextBoxInputMethodOptionKinds.DynamicTemplate:
          return this._configuration.dynamicTemplate;
      }
      if (!source.isFreeInputable && (source.isMultiLine || source.isNumeric)) {
        // 文書内転記が指定されている、もしくはフリー入力が不可に指定されているオブジェクト
        // フリー入力不可の場合に関連付けの色が適用されるのはV1のバグと思われるが、同様の挙動に合わせこんでいる
        // isMultiLineとisNumericの判定は、固定文字なのかどうかを判定するために使用している
        return this._configuration.related;
      }
      // 入力不可オブジェクト判定はNormalに対してのみ行う.
      if (!source.isFreeInputable) {
        // 編集できないtextは印刷時に塗りつぶすか否かに編集時の塗りつぶし要否も連動させる.
        // (本来フォーマットを直して対応すべき所を、v1が無理やり実装で対応してしまったか？)
        // 実際に徳大に以下の2種類のフォーマットが存在する.
        // ・印刷時背景色未指定、編集時背景色未指定で、円の上にテキストが配置されていて、透過しないと欠ける.
        // ->「経過報告書（文書料なし）_」
        // -> フォーマットの編集時背景色も透過指定されてればこんなことにはならない.
        // ・印刷時背景色指定、編集時背景色未指定で、塗りつぶしたいテキストの上にテキストが配置されていて、塗りつぶさないと文字が重なって見える.
        // ->「文書作成依頼書」
        // -> フォーマットの編集時背景色も塗りつぶし指定されてればこんなことにはならない.
        // v1挙動を再現できるよう印刷時背景色が指定されてるか否かでスタイルを変える.
        return this.isFilledOnPrinting(source)
          ? this._configuration.notEditable.filledOnPrinting
          : this._configuration.notEditable.notFilledOnPrinting;
        }
      } else if (source.isNumeric) {
      return this._configuration.numericInputting;
    }
    return this._configuration.default;
  }

  private isFilledOnPrinting(source: TextBoxItem): boolean {
    return source.paintStyle
        && source.paintStyle.color
        && source.paintStyle.color.value
        && (source.paintStyle.color.value !== "transparent");
  }

  public readTextStyle(source: TextBoxItem, shouldInputFree: boolean, isFocused: boolean): TextStyle {
    if (!source.isNumeric && (source.shouldInputFree || (source.isFreeInputable && shouldInputFree))) {
      return isFocused
        ? this.toFreeInputTextStyle(source)
        : this.toTextStyle(source);
    } else if (this.shouldDrawObjectName(source)) {
      return this.toObjectNameTextStyle(source);
    }
    return this.toTextStyle(source);
  }

  private shouldDrawObjectName(source: TextBoxItem): boolean {
    return source.isEmpty
        && ((source.inputMethodKind === TextBoxInputMethodKinds.None)
         || (source.inputMethodKind === TextBoxInputMethodKinds.HisInfoSelect)
         || (source.inputMethodKind === TextBoxInputMethodKinds.UserDefinitionSelect)
         || (source.inputMethodKind === TextBoxInputMethodKinds.MKSTemplate)
         || (source.inputMethodKind === TextBoxInputMethodKinds.DynamicTemplate));
  }

  protected readForegroundDisplayConfiguration(): IEnableConfiguration {
    return this._configuration.foreground;
  }
}
